import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, Copy, BookText, Settings2, X } from 'lucide-react';

export default function Rechnungsposten() {
  const { API } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showCatManager, setShowCatManager] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', category: '', unit: 'Stk.', unit_price: 0, tax_rate: 19 });

  const load = async () => {
    const [t, c] = await Promise.all([axios.get(`${API}/invoice-templates`), axios.get(`${API}/invoice-templates/categories`)]);
    setTemplates(t.data);
    setCategories(c.data.length ? c.data : ['Sonstiges']);
  };
  useEffect(() => { load(); }, [API]);

  const addCategory = async () => {
    if (!newCat.trim() || categories.includes(newCat.trim())) return;
    const updated = [...categories, newCat.trim()];
    await axios.put(`${API}/invoice-templates/categories`, { categories: updated });
    setCategories(updated);
    setNewCat('');
    toast.success('Kategorie hinzugefügt');
  };

  const deleteCategory = async (cat) => {
    const updated = categories.filter(c => c !== cat);
    await axios.put(`${API}/invoice-templates/categories`, { categories: updated });
    setCategories(updated);
    toast.success('Kategorie entfernt');
  };

  const filtered = templates.filter(t => {
    if (catFilter && t.category !== catFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return t.title?.toLowerCase().includes(s) || t.description?.toLowerCase().includes(s) || t.category?.toLowerCase().includes(s);
    }
    return true;
  });

  const grouped = {};
  filtered.forEach(t => { const c = t.category || 'Sonstiges'; (grouped[c] = grouped[c] || []).push(t); });

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', description: '', category: catFilter || '', unit: 'Stk.', unit_price: 0, tax_rate: 19 });
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ title: t.title, description: t.description, category: t.category || '', unit: t.unit, unit_price: t.unit_price, tax_rate: t.tax_rate });
    setShowForm(true);
  };

  const handleDuplicate = (t) => {
    setEditing(null);
    setForm({ title: t.title + ' (Kopie)', description: t.description, category: t.category, unit: t.unit, unit_price: t.unit_price, tax_rate: t.tax_rate });
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        const res = await axios.put(`${API}/invoice-templates/${editing.id}`, form);
        setTemplates(prev => prev.map(t => t.id === editing.id ? res.data : t));
        toast.success('Aktualisiert');
      } else {
        const res = await axios.post(`${API}/invoice-templates`, form);
        setTemplates(prev => [...prev, res.data]);
        toast.success('Rechnungsposten gespeichert');
      }
      setShowForm(false);
      const c = await axios.get(`${API}/invoice-templates/categories`);
      if (c.data.length) setCategories(c.data);
    } catch { toast.error('Fehler'); }
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/invoice-templates/${id}`);
    setTemplates(prev => prev.filter(t => t.id !== id));
    toast.success('Gelöscht');
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="rechnungsposten-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white tracking-tight">Rechnungsposten</h1>
        <Button onClick={openCreate} data-testid="add-template"
          className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm shadow-[0_0_15px_rgba(245,158,11,0.3)]">
          <Plus className="w-4 h-4 mr-2" />Neuer Posten
        </Button>
      </div>

      <p className="text-sm text-slate-500">Vorgefertigte Positionen für schnellere Angebots- und Rechnungserstellung.</p>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input data-testid="template-search" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Posten durchsuchen..." className="pl-10 bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600" />
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <Button size="sm" variant={!catFilter ? 'default' : 'secondary'} onClick={() => setCatFilter('')}
          className={!catFilter ? 'bg-amber-500 text-slate-950' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}>Alle</Button>
        {categories.map(c => (
          <Button key={c} size="sm" variant={catFilter === c ? 'default' : 'secondary'} onClick={() => setCatFilter(c)}
            className={catFilter === c ? 'bg-amber-500 text-slate-950' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}>{c}</Button>
        ))}
        <Button size="sm" variant="ghost" onClick={() => setShowCatManager(true)}
          className="text-slate-500 hover:text-amber-400"><Settings2 className="w-3.5 h-3.5" /></Button>
      </div>

      {/* Category Manager */}
      {showCatManager && (
        <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-white">Kategorien verwalten</h3>
            <button onClick={() => setShowCatManager(false)} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex gap-2">
            <Input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="Neue Kategorie..."
              onKeyDown={e => e.key === 'Enter' && addCategory()}
              className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 text-sm flex-1" />
            <Button size="sm" onClick={addCategory} className="bg-amber-500 text-slate-950 hover:bg-amber-400 rounded-sm"><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="flex gap-2 flex-wrap">
            {categories.map(c => (
              <span key={c} className="flex items-center gap-1 px-2 py-1 bg-slate-800 text-slate-300 rounded-sm text-xs">
                {c}
                <button onClick={() => deleteCategory(c)} className="text-slate-500 hover:text-rose-400 ml-1"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>
      )}

      {Object.keys(grouped).length ? Object.entries(grouped).map(([cat, items]) => (
        <div key={cat}>
          <div className="flex items-center gap-2 mb-2 mt-4">
            <BookText className="w-4 h-4 text-amber-500" strokeWidth={1.5} />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">{cat}</h3>
            <span className="text-xs font-mono text-slate-600">{items.length}</span>
          </div>
          <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm overflow-hidden">
            {items.map(t => (
              <div key={t.id} className="flex items-center gap-4 px-4 py-3 border-b border-slate-800/40 hover:bg-slate-900/80 transition-colors group"
                data-testid={`tpl-${t.id}`}>
                <div className="w-8 h-8 rounded-sm bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                  <BookText className="w-4 h-4 text-amber-400" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-200">{t.title}</p>
                  <p className="text-xs text-slate-500 line-clamp-2">{t.description}</p>
                </div>
                <span className="text-sm font-mono text-amber-400 shrink-0">{t.unit_price?.toFixed(2)} EUR / {t.unit}</span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => handleDuplicate(t)} className="text-slate-400 hover:text-blue-400 w-8 h-8" title="Duplizieren">
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(t)} className="text-slate-400 hover:text-amber-400 w-8 h-8">
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(t.id)} className="text-slate-400 hover:text-rose-400 w-8 h-8">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )) : <div className="p-8 text-center text-slate-500 bg-slate-900/50 border border-slate-800/60 rounded-sm">
        {search || catFilter ? 'Keine Posten gefunden' : 'Noch keine Rechnungsposten angelegt'}
      </div>}

      <Dialog open={showForm} onOpenChange={v => { setShowForm(v); if (!v) setEditing(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200 max-w-lg">
          <DialogHeader><DialogTitle className="text-white">{editing ? 'Posten bearbeiten' : 'Neuer Rechnungsposten'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Kurzbezeichnung</Label>
              <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                placeholder="z.B. Dachsanierung komplett" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
            </div>
            <div>
              <Label className="text-slate-300">Beschreibungstext (erscheint in Rechnung)</Label>
              <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={4}
                placeholder="Komplette Dachsanierung inkl. Abriss der alten Eindeckung, Entsorgung und Neueindeckung mit Tonziegeln nach DIN 18338"
                className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Kategorie</Label>
                <Input value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                  placeholder="z.B. Montage" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" list="tpl-cat-list" />
                <datalist id="tpl-cat-list">{categories.map(c => <option key={c} value={c} />)}</datalist>
              </div>
              <div>
                <Label className="text-slate-300">Einheit</Label>
                <Input value={form.unit} onChange={e => setForm({...form, unit: e.target.value})}
                  className="bg-slate-950 border-slate-800 text-slate-200 mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Preis (EUR)</Label>
                <Input type="number" step="0.01" value={form.unit_price} onChange={e => setForm({...form, unit_price: parseFloat(e.target.value)||0})}
                  className="bg-slate-950 border-slate-800 text-slate-200 mt-1" />
              </div>
              <div>
                <Label className="text-slate-300">MwSt. %</Label>
                <Input type="number" value={form.tax_rate} onChange={e => setForm({...form, tax_rate: parseFloat(e.target.value)||19})}
                  className="bg-slate-950 border-slate-800 text-slate-200 mt-1" />
              </div>
            </div>
            <Button onClick={handleSave} className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">
              {editing ? 'Speichern' : 'Posten anlegen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
